'use client';

import { Form, Nimbus3Scope } from '@gartner-digital-markets/nimbus-v3-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { identifyVisitorByEmail } from '../browser/identity';
import { createGatewaySubmitAction } from '../browser/submission';
import type {
  ClientDataContextProps,
  FormPayload,
  FormTracking,
  OnServerLog,
  SubmitFormResult,
} from '../core';
import { EmailField } from './components/EmailField';
import { FormFooter } from './components/FormFooter';
import { FormHeadline } from './components/FormHeadline';
import { FormModal } from './components/FormModal';
import { SubmissionErrorMessage } from './components/SubmissionErrorMessage';
import { SubmitButton } from './components/SubmitButton';
import { TextField } from './components/TextField';
import { useFormAnalytics } from './hooks/useFormAnalytics';
import { useFormSubmission } from './hooks/useFormSubmission';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Please enter a valid email address'),
});

type FormValues = z.infer<typeof formSchema>;

export interface VendorClaimProfileFormProps {
  formId: string;
  lang?: string;
  isOpen: boolean;
  onClose: () => void;
  headline?: string;
  subHeading?: string;
  buttonText?: string;
  successHeadline?: string;
  successMessage?: string;
  learnMoreUrl?: string;
  learnMoreText?: string;
  contextProps?: ClientDataContextProps;
  tracking?: FormTracking;
  gatewayUrl?: string;
  submitAction?: (payload: FormPayload) => Promise<SubmitFormResult>;
  onServerLog?: OnServerLog;
  termsUrl?: string;
  privacyUrl?: string;
}

export interface VendorClaimProfileFormBrandConfig {
  defaultTracking?: FormTracking;
  termsUrl?: string;
  privacyUrl?: string;
  submissionFailedMessageKey?: string;
}

interface SharedVendorClaimProfileFormProps
  extends VendorClaimProfileFormProps {
  brandConfig: VendorClaimProfileFormBrandConfig;
}

export const VendorClaimProfileForm = ({
  formId,
  lang,
  isOpen,
  onClose,
  headline = 'Manage this profile',
  subHeading = "Request access to modify the listing. You're only a few steps away from updating content, responding to reviews, launching a campaign, and more.",
  buttonText = 'REQUEST ACCESS',
  successHeadline = 'Thank you!',
  successMessage = 'A confirmation email of this request has been sent to your inbox.',
  learnMoreUrl = 'https://www.g2digitalmarkets.com/',
  learnMoreText = 'Learn more about G2 Digital Markets',
  contextProps,
  tracking,
  gatewayUrl = '/bx-capterra-api/bx-forms/submit',
  submitAction,
  onServerLog,
  termsUrl,
  privacyUrl,
  brandConfig,
}: SharedVendorClaimProfileFormProps) => {
  const [isSuccess, setIsSuccess] = useState(false);

  const mergedTracking = useMemo(
    () => ({
      ...(brandConfig.defaultTracking ?? {}),
      ...tracking,
    }),
    [brandConfig.defaultTracking, tracking]
  );

  const portalBridgeAttributes = useMemo(() => {
    const brand = brandConfig.defaultTracking?.brand;
    if (!brand) return undefined;
    return { 'data-bx-forms-brand': brand };
  }, [brandConfig.defaultTracking?.brand]);

  const resolvedSubmitAction = useMemo(
    () => submitAction ?? createGatewaySubmitAction(gatewayUrl),
    [submitAction, gatewayUrl]
  );

  const resolvedTermsUrl = termsUrl ?? brandConfig.termsUrl;
  const resolvedPrivacyUrl = privacyUrl ?? brandConfig.privacyUrl;

  const {
    submitToApi,
    resetSubmissionMetadata,
    submissionErrorMessage,
    isLoading,
  } = useFormSubmission({
    formId,
    tracking: mergedTracking,
    contextProps: {
      ...contextProps,
      pagePlacement: contextProps?.pagePlacement ?? 'modal',
    },
    templateName: 'vendor-claim-profile',
    submitAction: resolvedSubmitAction,
  });

  const analytics = useFormAnalytics(formId, 'email-form-cta', isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (!formId) {
      analytics.trackFormFailedToDisplay();
      return;
    }
    analytics.trackFormOpen();
    analytics.trackFormExposeServer({
      formTemplate: 'vendor-claim-profile',
      formKind: 'vendor-claim',
      brand: mergedTracking.brand,
      placement: contextProps?.pagePlacement,
      page_type: contextProps?.pageType,
      page_path: window.location.pathname,
      eloqua_campaign: mergedTracking.eloquaCampaign,
    });
  }, [
    analytics,
    contextProps,
    formId,
    isOpen,
    mergedTracking.brand,
    mergedTracking.eloquaCampaign,
  ]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
    },
    mode: 'onBlur',
  });

  const handleSubmissionFailure = (result: SubmitFormResult) => {
    onServerLog?.(
      JSON.stringify({
        messageKey:
          brandConfig.submissionFailedMessageKey ??
          'bx_forms_submission_failed',
        message: `Form submission failed: ${JSON.stringify(result)}`,
        formId,
        contextProps,
      })
    );
  };

  const handleSubmit = async (data: FormValues) => {
    const result = await submitToApi({
      ...data,
      hasSecondPage: false,
    });

    if (result.success) {
      identifyVisitorByEmail(data.emailAddress);
      analytics.trackFormSubmitted(buttonText);
      setIsSuccess(true);
      return;
    }

    analytics.trackFormFailed();
    handleSubmissionFailure(result);
  };

  const handleClose = () => {
    analytics.trackFormClosed(1, 'close-button');
    setIsSuccess(false);
    resetSubmissionMetadata();
    form.reset();
    onClose();
  };

  // Success screen
  const renderSuccess = () => (
    <div className="flex flex-col items-center gap-6 py-8 text-center">
      {/* Illustration placeholder - matches the design */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-nimbus-primary" />
          <span className="h-2 w-32 rounded bg-nimbus-muted" />
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-nimbus-muted" />
          <span className="h-1.5 w-28 rounded bg-nimbus-muted/60" />
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-red-500" />
          <span className="h-1.5 w-24 rounded bg-nimbus-muted/40" />
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-amber-500" />
          <span className="h-1.5 w-20 rounded bg-nimbus-muted/30" />
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-nimbus-foreground">
          {successHeadline}
        </h2>
        <p className="text-nimbus-muted-foreground">{successMessage}</p>
        <a
          href={learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block font-semibold text-nimbus-primary hover:underline"
        >
          {learnMoreText}
        </a>
      </div>
    </div>
  );

  // Form content
  const renderForm = () => (
    <>
      <FormHeadline title={headline} subtitle={subHeading} />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit, () =>
            analytics.trackFormFailed()
          )}
          className="space-y-6"
        >
          {/* First Name + Last Name: 2-column grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              name="firstName"
              label="First Name"
              required={true}
              onFocus={() => analytics.trackFormStarted('email-box-click')}
            />
            <TextField name="lastName" label="Last Name" required={true} />
          </div>

          {/* Email: full width */}
          <EmailField
            label="Business Email Address"
            onPointerDown={() => analytics.trackFormStarted('email-box-click')}
            onInput={() => analytics.trackFormStarted('email-box-type')}
          />

          <FormFooter
            lang={lang}
            termsUrl={resolvedTermsUrl}
            privacyUrl={resolvedPrivacyUrl}
          />

          <div className="flex justify-center">
            <SubmitButton
              text={buttonText}
              isLoading={isLoading}
              variant="accent"
              className="px-8"
            />
          </div>
          <SubmissionErrorMessage message={submissionErrorMessage} />
        </form>
      </Form>
    </>
  );

  return (
    <Nimbus3Scope
      portalBridgeAttributes={portalBridgeAttributes}
      style={{ background: 'transparent' }}
    >
      <FormModal
        open={isOpen}
        title={headline}
        description={subHeading}
        onClose={handleClose}
        className="sm:max-w-[560px]"
        dataTestId="vendor-claim-profile-form"
      >
        <section
          aria-label={`${headline} form`}
          onMouseEnter={() => analytics.trackFormMouseover()}
          role="group"
          className="bg-nimbus-background p-6 sm:p-8"
        >
          {isSuccess ? renderSuccess() : renderForm()}
        </section>
      </FormModal>
    </Nimbus3Scope>
  );
};
